import { Card } from '@/components/Elements'
import { Box, Flex, FormLabel, SimpleGrid } from '@chakra-ui/react'

import { DatePicker, Input, Select } from '@/components/Form'
import { ContributorGroupType } from '@/features/contributorGroups/constants'
import { useCustomer } from '@/features/customers/useCustomer'
import { ContributorStatus, OfferStatus } from '@/features/offers/constants'
import { SystemVariableType } from '@/features/systemVariables/constants'
import useSystemVariable from '@/features/systemVariables/useSystemVariable'
import useUser from '@/features/users/useUser'
import { useFormContext, useWatch } from 'react-hook-form'

const OfferOverviewOfferInfo = ({ isDisabled }) => {
  const { getValues, setValue } = useFormContext()

  const { useFetchAllCustomers } = useCustomer()
  const { useUserOptions } = useUser()
  const { useSystemVariableOptions } = useSystemVariable()

  const { data: fetchCustomerResponse } = useFetchAllCustomers({ page: 1, pageSize: 0 })

  const projectStartDate = useWatch({ name: 'projectStartDate' })
  const createdBy = useWatch({ name: 'createdBy' })
  const creator = useWatch({ name: 'creator' })
  const subSystems = useWatch({ name: 'subSystems' })
  const mainSubSystemType = useWatch({ name: 'subSystems.0.type' })
  const allSubSystemNewOrRenewal = useWatch({ name: 'subSystems.0.newOrRenewal' })
  const siemensRoleInTheOffer = useWatch({ name: 'siemensRoleInTheOffer' })
  const status = useWatch({ name: 'status' })

  //Translated
  const hasMaintenance = subSystems.filter((subsystem) => subsystem.type === 'maintenance').length > 0
  const { data: allUserOptions } = useUserOptions({ valueKey: '_id', status: 'active' })
  const { data: PMOptions } = useUserOptions({
    valueKey: '_id',
    status: 'active',
    contributorGroup: [
      ContributorGroupType.TECHNICAL_PROJECT_MANAGER,
      ContributorGroupType.SALES,
      ContributorGroupType.ENGINEER,
    ],
  })

  const { data: engineerUserOptions } = useUserOptions({
    valueKey: '_id',
    status: 'active',
    contributorGroup: [ContributorGroupType.TECHNICAL_PROJECT_MANAGER, 'engineer'],
  })

  const customerOptions = fetchCustomerResponse?.data
    ? fetchCustomerResponse.data
        .filter((customer) => !customer.isDeleted)
        .map((customer) => ({
          value: customer._id,
          label: `${customer?.code ?? 'NO CUST CODE'}-${customer?.name}`,
        }))
    : []

  const { data: SISICOptions } = useSystemVariableOptions(SystemVariableType.SISIC, {
    status: 'active',
    valueKey: '_id',
    labelKey: (sisic) => `${sisic.code} - ${sisic.name}`,
  })

  //Handler
  const handleManagerOnChange = (val) => {
    const contributors = getValues('contributors')
    const technicalProjectManagerContributorDetailIndex = contributors.findIndex(
      (detail) =>
        detail.group === ContributorGroupType.TECHNICAL_PROJECT_MANAGER &&
        !detail.hasEdit &&
        detail.status === ContributorStatus.REQUIRED
    )

    if (technicalProjectManagerContributorDetailIndex === -1) {
      return
    }

    if (status !== OfferStatus.QUOTATION_DRAFT) {
      return
    }

    setValue(`contributors.${technicalProjectManagerContributorDetailIndex}.user._id`, val, { shouldDirty: true })
  }

  return (
    <Card title={'Offer Info'} hasBorder={false} canCollapse defaultIsOpen>
      <SimpleGrid columns={[2, null, 2]} spacingX="40px" spacingY="15px">
        <DatePicker name="offerSubmissionDate" label="Offer Submission Date" disabled={isDisabled} isRow needPortal />
        <Flex alignItems={'center'}>
          <FormLabel w="30%">
            <Flex gap={1}>
              <Box>Project Duration</Box>
              <Box color="red">*</Box>
            </Flex>
          </FormLabel>
          <Box w="30%">
            <DatePicker name="projectStartDate" labelWidth="0" disabled={isDisabled} isRow />
          </Box>
          <Box w="5%">To</Box>

          <Box w="30%">
            <DatePicker
              name="projectEndDate"
              labelWidth="0"
              disabled={isDisabled}
              minDate={projectStartDate}
              isRow
              isRequired={false}
            />
          </Box>
        </Flex>

        <Select
          name={`manager`}
          label="Project Manager"
          disabled={isDisabled}
          options={PMOptions}
          isRow
          extraOnChange={handleManagerOnChange}
        />
        <Select
          name={`engineers`}
          label="Engineer(s)"
          disabled={isDisabled}
          options={engineerUserOptions}
          isMulti
          isRow
          isRequired={false}
        />
        {mainSubSystemType === 'maintenance' && (
          <Flex gap={2}>
            <Box w="65%">
              <Select
                name={`subSystems.0.newOrRenewal`}
                label="New/Renewal"
                disabled={isDisabled}
                labelWidth="46.5"
                options={[
                  { value: 'new', label: 'New' },
                  { value: 'renewal', label: 'Renewal' },
                  {
                    value: 'renewalOfDiscontinued',
                    label: 'Renewal Of Discontinued',
                  },
                ]}
                isRow
              />
            </Box>

            <Box>
              {allSubSystemNewOrRenewal === 'new' && (
                <Input
                  name={`subSystems.0.conversionRate`}
                  label="Conversion Rate (%)"
                  type="number"
                  placeholder="Enter value"
                  disabled={isDisabled}
                  isRow
                />
              )}
            </Box>
          </Flex>
        )}

        <Flex gap="3">
          <Select
            name="siemensRoleInTheOffer"
            label="Siemens Role In The Offer"
            disabled={isDisabled}
            options={[
              { value: 'mainContract', label: 'Main Contractor' },
              { value: 'subContract', label: 'Sub Contractor' },
              { value: 'other', label: 'Other' },
            ]}
            isRow
          />
          {siemensRoleInTheOffer === 'other' && <Input name="siemensRoleInTheOfferOther" disabled={isDisabled} isRow />}
        </Flex>

        <Select
          name="requirePhysicalSignature"
          label="Physical Signature"
          disabled={isDisabled}
          options={[
            { value: 'required', label: 'Required' },
            { value: 'notRequired', label: 'Not Required' },
          ]}
          isRow
        />
        <Select
          name="endCustomer"
          label="End Customer"
          disabled={isDisabled}
          options={customerOptions}
          isRow
          isRequired={true}
        />
        {hasMaintenance && (
          <Input
            name="optionToExtend"
            label="Maintenance Contract Option to Extend"
            placeholder="months"
            type="number"
            min="0"
            disabled={isDisabled}
            isRequired={false}
            isRow
          />
        )}

        <Select name="SISIC" label="SISIC" disabled={isDisabled} options={SISICOptions} isRow isRequired={true} />

        <Input name="LoANumber" label="LoA Number" disabled={isDisabled} isRequired={false} isRow />
      </SimpleGrid>
    </Card>
  )
}

export default OfferOverviewOfferInfo
